/**
 * Leads — list page rendered 1:1 in the design-folder style.
 *
 * HTML structure verbatim from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/leads.html
 *
 * Layout:
 *   1. `.projects-header` with title + stats + view toggle + "+ New Lead"
 *   2. Filter-chip row (All / 🔥 Hot / High / Ready to Move / Under Construction)
 *   3. Either board (7-col Kanban) OR list (`.proj-table`)
 *   4. Right-side slide-over preview (`<LeadSlideOver />`)
 *
 * Real data via `useLeads()`. `+ New Lead` navigates to /leads/new.
 * Clicking a card / row opens the slide-over (URL syncs to /leads/{id}).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { canCRMWrite } from '@shared/perms';

import { LeadSlideOver } from '@/components/LeadSlideOver';
import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import { updateLead } from '@/lib/leads';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import {
  budgetRangeLabel,
  isActiveLead,
  leadSourceLabel,
  possessionStatusLabel,
  projectTypeLabel,
  useLeads,
  type LeadListItem,
  type LeadPriority,
  type LeadSource,
  type LeadStatus,
} from '@/lib/useLeads';

// ───── Stage column model (folder labels — all 7) ─────

const BOARD_COLUMNS: { status: LeadStatus; label: string }[] = [
  { status: 'new',                  label: 'New Request' },
  { status: 'contacted',            label: 'Contacted' },
  { status: 'site_visit_scheduled', label: 'Site Visit Done' },
  { status: 'proposal_sent',        label: 'Proposal Sent' },
  { status: 'negotiation',          label: 'Negotiation' },
  { status: 'converted',            label: 'Converted' },
  { status: 'lost',                 label: 'Lost' },
];

/** Maps our 7 statuses → iio.css stage-pill modifier (list view). */
function stagePillClass(s: LeadStatus): string {
  switch (s) {
    case 'new':                   return 'stage-discovery';
    case 'contacted':             return 'stage-approval';
    case 'site_visit_scheduled':  return 'stage-design';
    case 'proposal_sent':         return 'stage-execution';
    case 'negotiation':           return 'stage-handover';
    case 'converted':             return 'stage-completed';
    case 'lost':                  return 'stage-completed';
  }
}

function statusLabel(s: LeadStatus): string {
  switch (s) {
    case 'new':                  return 'New Request';
    case 'contacted':            return 'Contacted';
    case 'site_visit_scheduled': return 'Site Visit';
    case 'proposal_sent':        return 'Proposal Sent';
    case 'negotiation':          return 'Negotiation';
    case 'converted':            return 'Converted';
    case 'lost':                 return 'Lost';
  }
}

// ───── Priority badge (top-right corner of every kanban card) ─────
// Exact colours from the folder's inline styles.

const PRIORITY_BADGE: Record<
  LeadPriority,
  { label: string; bg: string; fg: string }
> = {
  hot:    { label: 'HOT',  bg: '#FEE2E2', fg: '#DC2626' },
  high:   { label: 'HIGH', bg: '#E0E7FF', fg: '#4F46E5' },
  medium: { label: 'MED',  bg: '#FEF3C7', fg: '#D97706' },
  low:    { label: 'LOW',  bg: '#DBEAFE', fg: '#2563EB' },
};

function PriorityBadge({ priority }: { priority: LeadPriority }) {
  const p = PRIORITY_BADGE[priority];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: 4,
        background: p.bg,
        color: p.fg,
        flexShrink: 0,
      }}
    >
      {p.label}
    </span>
  );
}

// ───── Source → Phosphor icon class (matches the folder mock) ─────

function sourceIcon(s: LeadSource): string {
  switch (s) {
    case 'instagram':  return 'ph ph-instagram-logo';
    case 'website':    return 'ph ph-globe';
    case 'reference':  return 'ph ph-users';
    case 'walk_in':    return 'ph ph-storefront';
    case 'just_dial':  return 'ph ph-phone';
    case 'google_ads': return 'ph ph-google-logo';
    case 'other':      return 'ph ph-envelope';
  }
}

// ───── Helpers ─────

const THUMB_COLORS: { bg: string; fg: string }[] = [
  { bg: '#FEE2E2', fg: '#DC2626' }, // red (brand)
  { bg: '#DBEAFE', fg: '#2563EB' }, // blue
  { bg: '#F3E8FF', fg: '#7C3AED' }, // purple
  { bg: '#FCE7F3', fg: '#DB2777' }, // pink
  { bg: '#D1FAE5', fg: '#059669' }, // green
  { bg: '#FEF3C7', fg: '#B45309' }, // amber
];
function pickThumb(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return THUMB_COLORS[Math.abs(h) % THUMB_COLORS.length];
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function compactInr(n: number | undefined | null): string {
  if (!n || n <= 0) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${Math.round(n / 100000)}L`;
  if (n >= 1000)     return `₹${Math.round(n / 1000)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
}

/** Prefer the bucketed `budgetRange` label when present; fall back
 *  to the legacy free-number `budget` formatted compactly. */
function leadBudgetText(l: LeadListItem): string {
  if (l.budgetRange) return budgetRangeLabel(l.budgetRange);
  return compactInr(l.budget);
}

function relTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

/** "Today" / "Tomorrow" / "24 May" — relative for follow-up footer. */
function followUpRel(ms: number | null | undefined): string {
  if (!ms) return 'No follow-up';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(ms);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (days === 0)  return 'Today';
  if (days === 1)  return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return due.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

/** Card subtitle line — `projectType · location` per the folder
 *  ("3BHK Renovation · HSR Layout"). Falls back to phone if neither. */
function cardSubtitle(l: LeadListItem): string {
  const parts: string[] = [];
  if (l.projectType) parts.push(projectTypeLabel(l.projectType));
  if (l.location) parts.push(l.location);
  return parts.length ? parts.join(' · ') : l.phone || '—';
}

// ───── Filter chips (above board/list) ─────

type FilterChip = 'all' | 'hot' | 'high' | 'ready_to_move' | 'under_construction';

const FILTER_CHIPS: { key: FilterChip; label: string; icon?: string; iconColor?: string }[] = [
  { key: 'all',                label: 'All Leads' },
  { key: 'hot',                label: 'Hot Priority',  icon: 'ph-bold ph-fire', iconColor: 'var(--brand)' },
  { key: 'high',               label: 'High Priority' },
  { key: 'ready_to_move',      label: 'Ready to Move' },
  { key: 'under_construction', label: 'Under Construction' },
];

function matchesFilter(l: LeadListItem, f: FilterChip): boolean {
  switch (f) {
    case 'all':                return true;
    case 'hot':                return l.priority === 'hot';
    case 'high':               return l.priority === 'high';
    case 'ready_to_move':      return l.possessionStatus === 'ready_to_move';
    case 'under_construction': return l.possessionStatus === 'under_construction';
  }
}

// ───── Date range filter ─────

type DateRange = 'all' | 'last_7d' | 'last_30d' | 'last_90d' | 'last_year';

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: 'all',        label: 'All time' },
  { value: 'last_7d',    label: 'Last 7 days' },
  { value: 'last_30d',   label: 'Last 30 days' },
  { value: 'last_90d',   label: 'Last 90 days' },
  { value: 'last_year',  label: 'Last 12 months' },
];

function dateRangeCutoff(r: DateRange): number {
  const now = Date.now();
  switch (r) {
    case 'last_7d':   return now - 7 * 86400000;
    case 'last_30d':  return now - 30 * 86400000;
    case 'last_90d':  return now - 90 * 86400000;
    case 'last_year': return now - 365 * 86400000;
    case 'all':       return 0;
  }
}

/** Client-side text search — name / phone / email / location / notes. */
function matchesSearch(l: LeadListItem, q: string): boolean {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    l.name,
    l.phone,
    l.email ?? '',
    l.location ?? '',
    l.notes ?? '',
    leadSourceLabel(l.source),
  ].join(' ').toLowerCase();
  return haystack.includes(needle);
}

// ───── Page ─────

export function LeadsPage() {
  const { leads, loading, error, orgId, hasMore, loadMore, loaded } = useLeads();
  const [view, setView] = useState<'board' | 'list'>('board');
  const [chip, setChip] = useState<FilterChip>('all');
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  const navigate = useNavigate();
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canCRMWrite(orgRole);

  // Tell the topbar what placeholder to show while we're on this page.
  useEffect(() => {
    setPlaceholder('Search leads by name, phone, email…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);
  // Slide-over open state is driven by the URL: visiting /leads/{id}
  // auto-opens the panel for that lead. Closing the panel resets to
  // /leads (which preserves the list view + filter chip selection).
  const { leadId: openLeadId } = useParams<{ leadId?: string }>();
  const [openId, setOpenId] = useState<string | null>(openLeadId ?? null);
  useEffect(() => {
    setOpenId(openLeadId ?? null);
  }, [openLeadId]);

  // ── Drag-and-drop state (board view only) ────────────────────────
  //
  // Pattern mirrors `TimelineBoardView.tsx` exactly:
  //   - `draggingId` dims the card being moved + suppresses the
  //     normal click→open behavior so the user doesn't open the
  //     slide-over on drag-end.
  //   - `dragOver` highlights the column the pointer is over so the
  //     user has a clear drop target.
  //   - On drop, we call `updateLead(id, { status })` — a one-field
  //     write. The live `useLeads` snapshot picks up the change and
  //     re-buckets the card into the target column within ~1s.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<LeadStatus | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  async function handleDropOnColumn(target: LeadStatus, leadId: string) {
    setDragOver(null);
    setDraggingId(null);
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.status === target) return;
    try {
      await updateLead(leadId, { status: target });
    } catch (e) {
      setMoveError((e as Error).message);
    }
  }

  // Summary numbers for the header sub-text.
  const summary = useMemo(() => {
    let active = 0;
    let pipelineInr = 0;
    for (const l of leads) {
      if (isActiveLead(l.status)) {
        active += 1;
        pipelineInr += l.budget ?? 0;
      }
    }
    return { active, pipelineInr };
  }, [leads]);

  // Apply chip + date-range + search filters (all combine; used by
  // both board and list views).
  const filteredLeads = useMemo(() => {
    const cutoff = dateRangeCutoff(dateRange);
    return leads.filter((l) => {
      if (!matchesFilter(l, chip)) return false;
      if (cutoff > 0) {
        const ms = l.createdAt?.toMillis?.() ?? 0;
        if (ms < cutoff) return false;
      }
      if (!matchesSearch(l, searchQuery)) return false;
      return true;
    });
  }, [leads, chip, dateRange, searchQuery]);

  // Bucket filtered leads per kanban column.
  const buckets = useMemo(() => {
    const m = new Map<LeadStatus, LeadListItem[]>();
    for (const col of BOARD_COLUMNS) m.set(col.status, []);
    for (const l of filteredLeads) {
      const arr = m.get(l.status);
      if (arr) arr.push(l);
    }
    return m;
  }, [filteredLeads]);

  const open = (id: string) => {
    setOpenId(id);
    navigate(`/leads/${id}`);
  };
  const closeSlideOver = () => {
    setOpenId(null);
    navigate('/leads');
  };

  return (
    <div className="page active" id="page-leads" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Leads</h1>
          <div className="projects-stats">
            <span>{summary.active}</span> active leads ·{' '}
            <span>{compactInr(summary.pipelineInr)}</span> pipeline
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="view-toggle">
            <button
              type="button"
              className={`view-btn${view === 'board' ? ' active' : ''}`}
              onClick={() => setView('board')}
            >
              <i className="ph ph-kanban" /> Board
            </button>
            <button
              type="button"
              className={`view-btn${view === 'list' ? ' active' : ''}`}
              onClick={() => setView('list')}
            >
              <i className="ph ph-list" /> List
            </button>
          </div>
          {canWrite ? (
            // Hidden for roles that can't write — Firestore rule blocks it anyway
            <button
              className="btn-primary"
              type="button"
              onClick={() => navigate('/leads/new')}
              disabled={!orgId}
            >
              <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> New Lead
            </button>
          ) : null}
        </div>
      </div>

      {/* Filter chip row + date-range dropdown. */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 20,
          overflowX: 'auto',
          paddingBottom: 8,
          alignItems: 'center',
        }}
        className="hide-scrollbar"
      >
        {FILTER_CHIPS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`filter-chip${chip === f.key ? ' active' : ''}`}
            onClick={() => setChip(f.key)}
          >
            {f.icon ? (
              <i className={f.icon} style={f.iconColor ? { color: f.iconColor } : undefined} />
            ) : null}
            {f.label}
          </button>
        ))}

        <div className="filter-sep" />

        {/* Date range — same `.filter-select` styling as projects.html
            uses for its sort dropdown. */}
        <select
          className="filter-select"
          value={dateRange}
          onChange={(e) => setDateRange(e.currentTarget.value as DateRange)}
          aria-label="Date range"
        >
          {DATE_RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>📅 {o.label}</option>
          ))}
        </select>

        {/* Filter summary — shows when something narrows the view. */}
        {(searchQuery || chip !== 'all' || dateRange !== 'all') ? (
          <span
            style={{
              fontSize: 12,
              color: 'var(--text-tertiary)',
              marginLeft: 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            Showing {filteredLeads.length} of {loaded}
            {hasMore ? '+' : ''}
          </span>
        ) : null}
      </div>

      {/* Loading / error / empty state */}
      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading leads…
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--critical)' }}>
          Couldn't load leads: {error}
        </div>
      ) : leads.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
            <i className="ph ph-funnel" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            No leads yet
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Capture every inquiry your studio receives and track them through the pipeline.
          </div>
          {canWrite ? (
            // Hidden for roles that can't write — Firestore rule blocks it anyway
            <button className="btn-primary" type="button" onClick={() => navigate('/leads/new')}>
              <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> Add your first lead
            </button>
          ) : null}
        </div>
      ) : view === 'board' ? (
        <div id="leads-board-view">
          {moveError ? (
            <div
              className="card"
              style={{
                padding: 12,
                marginBottom: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 13,
              }}
            >
              <span>Couldn't move lead: {moveError}</span>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setMoveError(null)}
              >
                Dismiss
              </button>
            </div>
          ) : null}
          <div className="kanban-board">
            {BOARD_COLUMNS.map((col) => {
              const rows = buckets.get(col.status) ?? [];
              const isOver = dragOver === col.status;
              return (
                <div
                  className="kanban-col"
                  key={col.status}
                  // preventDefault on dragOver is REQUIRED — HTML5 DnD's
                  // default behavior is "no drop allowed" until you
                  // explicitly opt in.
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragOver !== col.status) setDragOver(col.status);
                  }}
                  onDragLeave={() => {
                    if (dragOver === col.status) setDragOver(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData('text/plain');
                    if (id) void handleDropOnColumn(col.status, id);
                  }}
                  style={{
                    background: isOver ? 'var(--brand-bg)' : undefined,
                    borderRadius: 8,
                    transition: 'background 0.15s',
                  }}
                >
                  <div className="kanban-header">
                    <span className="kanban-title">{col.label}</span>
                    <span className="kanban-count">{rows.length}</span>
                  </div>
                  {rows.length === 0 ? (
                    <div
                      style={{
                        border: '1px dashed var(--border)',
                        borderRadius: 8,
                        padding: 16,
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                        textAlign: 'center',
                        margin: '4px 0',
                      }}
                    >
                      Drop leads here
                    </div>
                  ) : null}
                  {rows.map((l) => {
                    const isDragging = draggingId === l.id;
                    return (
                      <div
                        key={l.id}
                        className="kanban-card"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', l.id);
                          e.dataTransfer.effectAllowed = 'move';
                          setDraggingId(l.id);
                        }}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDragOver(null);
                        }}
                        onClick={() => {
                          // Don't open the slide-over if the click
                          // is actually the tail of a drag interaction.
                          if (draggingId) return;
                          open(l.id);
                        }}
                        style={{
                          cursor: isDragging ? 'grabbing' : 'grab',
                          opacity: isDragging ? 0.45 : 1,
                          transform: isDragging ? 'rotate(1deg)' : undefined,
                          transition: 'opacity 0.15s, transform 0.15s',
                          ...(col.status === 'negotiation'
                            ? { borderLeft: '3px solid var(--brand)' }
                            : null),
                        }}
                        title="Drag to move · click to open"
                      >
                        {/* Title + priority badge */}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: 4,
                          }}
                        >
                          <div className="k-card-title" style={{ marginBottom: 0 }}>
                            {l.name}
                          </div>
                          <PriorityBadge priority={l.priority} />
                        </div>

                        {/* Subtitle */}
                        <div className="k-card-type">{cardSubtitle(l)}</div>

                        {/* Value + source meta */}
                        <div className="k-card-meta" style={{ marginBottom: 8 }}>
                          <span className="k-card-val">{leadBudgetText(l)}</span>
                          <span className="k-card-src">
                            <i className={sourceIcon(l.source)} />{' '}
                            {leadSourceLabel(l.source)}
                          </span>
                        </div>

                        {/* Follow-up footer */}
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            borderTop: '1px solid var(--border)',
                            paddingTop: 8,
                          }}
                        >
                          <i className="ph ph-calendar-blank" /> Follow-up:{' '}
                          {followUpRel(l.followUpAt?.toMillis() ?? null)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div id="leads-list-view">
          <div className="proj-table-wrap">
            <table className="proj-table">
              <thead>
                <tr>
                  <th>Lead Name</th>
                  <th>Stage</th>
                  <th>Possession</th>
                  <th className="right">Value</th>
                  <th>Source</th>
                  <th>Last Activity</th>
                  <th className="center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => {
                  const thumb = pickThumb(l.name);
                  const subtitle = cardSubtitle(l);
                  return (
                    <tr
                      key={l.id}
                      className="clickable-row"
                      onClick={() => open(l.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="proj-name-cell">
                          <div
                            className="proj-thumb"
                            style={{ background: thumb.bg, color: thumb.fg }}
                          >
                            {initials(l.name)}
                          </div>
                          <div className="proj-name-text">
                            <div className="proj-name-title">{l.name}</div>
                            <div className="proj-name-client">{subtitle}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`stage-pill ${stagePillClass(l.status)}`}>
                          {statusLabel(l.status)}
                        </span>
                      </td>
                      <td>
                        {l.possessionStatus
                          ? possessionStatusLabel(l.possessionStatus)
                          : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                      <td className="right tn">{leadBudgetText(l)}</td>
                      <td>{leadSourceLabel(l.source)}</td>
                      <td>
                        <span className="updated-text">
                          {relTime(l.createdAt?.toMillis() ?? null)}
                        </span>
                      </td>
                      <td className="center">
                        <button
                          className="row-menu-btn"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            open(l.id);
                          }}
                          aria-label="Open lead"
                        >
                          <i className="ph ph-caret-right" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* "Load more" footer — bumps the live window when the studio
          has more leads than the current page size. Shows a quiet
          "that's all" once we've reached the end. */}
      {leads.length > 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            padding: '24px 0 8px',
            color: 'var(--text-tertiary)',
            fontSize: 12,
          }}
        >
          <span>
            Loaded {loaded} lead{loaded === 1 ? '' : 's'}
            {hasMore ? '' : ' — that\'s all'}
          </span>
          {hasMore ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={loadMore}
              disabled={loading}
              style={{ height: 32, padding: '0 14px', fontSize: 12 }}
            >
              {loading ? 'Loading…' : 'Load 200 more'}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Right-side preview slide-over (matches leads.html's
          `.slide-over` panel). Opens when a card / row is clicked or
          when the URL has a `/leads/{id}` segment. */}
      <LeadSlideOver leadId={openId} onClose={closeSlideOver} />
    </div>
  );
}

